export default ({ tenantId = null, channelId = null } = {}) => {
  let unsubscribe = null
  let unsubscribeMessage = null
  let wireId = null
  let root = null
  let echoChannel = null
  let echo = null
  let connection = null
  let connectionHandler = null
  let visibilityHandler = null
  let onlineHandler = null
  let navigatingHandler = null
  let pageHideHandler = null
  let pageShowHandler = null
  let refreshing = false
  let pendingRefresh = null
  let markedThrough = 0
  let marking = false
  let receiptTimer = null
  let pendingReceipt = 0
  let lastReceiptAt = 0
  let reconnectTimer = null
  let subscribed = false
  let catchingUp = false
  let catchupPending = false
  const pendingIds = new Map()
  let destroyed = false
  let pendingSend = false
  let opened = false
  let viewportReady = false
  let refreshKind = 'created'
  let deferredReentry = false
  let hiddenPosition = null
  let lastPosition = null
  let lastScrollTop = 0
  let touchY = null
  let draggingScrollbar = false
  let olderVisible = false
  let olderIntent = false
  let olderPosition = null
  const channelName = `conversations.${tenantId}.${channelId}`

  return {
    busy: false,
    sending: false,
    failedAction: null,
    composing: false,
    tabVisible: false,
    visible: false,
    editingId: null,
    editDraft: '',
    editComposing: false,
    editSaving: false,
    olderLoading: false,
    restoring: false,
    accessLost: false,
    validationError: '',
    editError: '',

    get sendFailed() {
      return this.failedAction === 'send'
    },

    connect(wire) {
      root = this.$root
      wireId = root.closest('[wire\\:id]')?.getAttribute('wire:id')
      this.restoring = root.dataset.conversationRestore === 'true'
      navigatingHandler = () => this.concealForRestore()
      pageHideHandler = () => this.concealForRestore()
      pageShowHandler = event => {
        if (!event.persisted) return
        this.concealForRestore()
        this.requestRefresh('restore')
      }
      document.addEventListener('livewire:navigating', navigatingHandler)
      window.addEventListener('pagehide', pageHideHandler)
      window.addEventListener('pageshow', pageShowHandler)
      visibilityHandler = () => this.updateVisibility()
      onlineHandler = () => this.updateVisibility()
      document.addEventListener('visibilitychange', visibilityHandler)
      window.addEventListener('online', onlineHandler)
      window.addEventListener('offline', visibilityHandler)
      unsubscribe = wire.intercept(({ action, onSend, onError, onFailure, onFinish }) => {
        onSend(() => {
          if (['send', 'editMessage'].includes(action.name)) this.busy = true
          if (action.name === 'send') this.sending = true
          if (action.name === 'send' || !this.sendFailed) this.failedAction = null
        })
        onError(({ preventDefault, response }) => {
          preventDefault()
          this.failedAction = action.name
          pendingSend = false
          if ([403, 404].includes(response?.status)) this.clearContents()
          else if (response?.status === 419) this.leaveChannel()
        })
        onFailure(() => {
          this.failedAction = action.name
          pendingSend = false
        })
        onFinish(() => {
          if (['send', 'editMessage'].includes(action.name)) this.busy = false
          if (action.name === 'send') this.sending = false
          if (destroyed || !root.isConnected || this.accessLost) return
          this.reconcileEdit()
          if (this.failedAction) {
            pendingSend = false
            return
          }
          if (pendingSend && this.visible) {
            pendingSend = false
            root.querySelector('[data-test="conversation-composer"]')?.requestSubmit()
            return
          }
          if (pendingRefresh) this.requestRefresh()
          this.markDisplayed()
        })
      })
      // The global API has a working unsubscribe in Livewire 4.4.7; its
      // component message interceptor calls a missing WeakBag.delete on teardown.
      unsubscribeMessage = window.Livewire.interceptMessage(({ message, onSuccess, onError }) => {
        if (message.component.id !== wireId) {
          const dialog = message.component.$wire
          if (dialog?.actionName === 'delete_channel_message' &&
            dialog.actionContext?.tenant_id === tenantId && dialog.actionContext?.channel_id === channelId) {
            onError(({ response, preventDefault }) => {
              if (![403, 404].includes(response?.status)) return
              preventDefault()
              dialog.$set('visible', false, false)
              this.clearContents()
            })
          }
          return
        }
        onSuccess(({ payload, onMorphed }) => {
          if (!payload.effects.html) return
          const actions = [...message.actions].map(action => action.name)
          const kind = actions.includes('send')
            ? 'send'
            : actions.includes('reenterConversation')
              ? 'reenter'
              : actions.includes('refreshCurrentMessages') ? refreshKind : actions[0]
          const position = this.capturePosition()
          if (position && olderPosition?.olderFocused) position.olderFocused = true
          olderIntent = false
          draggingScrollbar = false
          onMorphed(() => {
            this.resizeComposer(root.querySelector('[data-test="conversation-body"]'))
            this.afterRender(kind, position)
          })
        })
      })
      this.resizeComposer(root.querySelector('[data-test="conversation-body"]'))
    },

    initializeViewport() {
      viewportReady = true
      if (!this.visible || opened || this.restoring || this.accessLost) return
      this.alignEntry()
      opened = true
      lastPosition = this.capturePosition()
      lastScrollTop = lastPosition?.scrollTop || 0
      this.markDisplayed()
    },

    alignEntry() {
      const list = root?.querySelector('[data-test="conversation-messages"]')
      if (!list) return
      olderIntent = false
      const marker = list.querySelector('[data-test="conversation-new-divider"], [data-test="conversation-new-above"]')
      if (marker) list.scrollTop += marker.getBoundingClientRect().top - list.getBoundingClientRect().top
      else this.scrollLatest()
    },

    _livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    setTabVisible(visible) {
      this.tabVisible = visible
      this.updateVisibility()
    },

    updateVisibility() {
      const visible = this.tabVisible && document.visibilityState !== 'hidden' && navigator.onLine
      if (destroyed || this.accessLost || visible === this.visible) return
      this.visible = visible
      if (visible) {
        if (this.restoring) {
          this.requestRefresh('restore')
        } else if (!opened) {
          if (viewportReady) this.initializeViewport()
        } else {
          this.restorePosition(hiddenPosition)
          this.requestRefresh('reenter')
        }
        this.subscribe()
      } else {
        this.flushReceipt()
        const list = root.querySelector('[data-test="conversation-messages"]')
        hiddenPosition = list?.clientHeight ? this.capturePosition() : lastPosition
        pendingRefresh = null
        pendingSend = false
        olderIntent = false
        this.leaveChannel()
      }
    },

    subscribe() {
      if (this.accessLost || echoChannel || !window.Echo || !tenantId || !channelId) return
      echo = window.Echo
      echoChannel = echo.private(channelName)
        .listen('.channel.message-created', signal => {
          this.receive(signal, 'created')
        })
        .listen('.channel.message-updated', signal => {
          this.receive(signal, 'updated')
        })
      echoChannel.subscribed(() => {
        subscribed = true
        this.scheduleCatchup()
      })
      echoChannel.error(error => {
        if ([403, 404].includes(Number(error?.status))) this.clearContents()
        else if (this.visible) this.failedAction = 'reconcileMessages'
      })
      connection = echo.connector?.pusher?.connection
      connectionHandler = () => {
        // A private channel's subscription callback runs only after fresh broker auth.
        subscribed = false
      }
      connection?.bind('connected', connectionHandler)
    },

    leaveChannel() {
      subscribed = false
      clearTimeout(reconnectTimer)
      reconnectTimer = null
      connection?.unbind('connected', connectionHandler)
      if (echoChannel) echo?.leave(channelName)
      echoChannel = null
      echo = null
      connection = null
      connectionHandler = null
    },

    async receive(signal, kind) {
      if (!this.visible || this.restoring || this.accessLost || destroyed || signal?.tenant_id !== tenantId ||
        signal?.channel_id !== channelId) return
      const id = signal.id ?? signal.message_id
      if (!Number.isSafeInteger(id) || id < 1) return
      const existing = root.querySelector('[data-test="conversation-list"]')?.querySelector('[data-message-id="' + id + '"]')
      if ((kind === 'created' && existing) || (kind === 'updated' && !existing)) return
      if (signal.id) return this.applyMessage(signal, kind)
      if (kind === 'created' && pendingIds.has(id)) return
      // ID-only mutations have no revision to deduplicate. Serialize each ID's
      // fetches so a later edit/delete cannot be dropped behind an in-flight read.
      const request = (pendingIds.get(id) || Promise.resolve()).then(async() => {
        if (!this.visible || this.restoring || this.accessLost || destroyed) return
        const messages = await this._livewire()?.fetchMessages([id])
        if (this.visible && !this.restoring && !this.accessLost && !destroyed) {
          messages?.forEach(message => this.applyMessage(message, kind))
        }
      }).catch(() => {
        // Fresh authorization failures are handled by the request interceptor.
      })
      pendingIds.set(id, request)
      try {
        await request
      } finally {
        if (pendingIds.get(id) === request) pendingIds.delete(id)
      }
    },

    scheduleCatchup() {
      if (reconnectTimer || !subscribed || !this.visible || this.restoring || this.accessLost) return
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        this.catchUp()
      }, 0)
    },

    async catchUp() {
      if (!this.visible || this.restoring || this.accessLost || destroyed) return
      if (catchingUp || refreshing) {
        catchupPending = true
        return
      }
      const wire = this._livewire()
      if (!wire) return
      catchingUp = true
      catchupPending = false
      const rows = [...root.querySelectorAll('[data-test="conversation-list"] [data-message-id]')]
      const oldest = Number(rows[0]?.dataset.messageId || 0)
      const latest = Number(rows.at(-1)?.dataset.messageId || 0)
      try {
        const result = await wire.reconcileMessages(oldest, latest)
        if (!this.visible || this.restoring || this.accessLost || destroyed) return
        if (!result || result.fallback) return this.requestRefresh()
        const changed = []
        for (const version of result.versions) {
          const row = root.querySelector('[data-test="conversation-list"] [data-message-id="' + version.id + '"]')
          if (!row) return this.requestRefresh()
          if (version.deleted) {
            this.applyTombstone(row, version.updated_at)
          } else if (row.dataset.updatedAt !== version.updated_at || version.edited) {
            // SQL timestamps have second precision. Refetch edited rows even
            // on equality, so two edits within one second cannot be missed.
            changed.push(version.id)
          }
        }
        if (changed.length > 50) return this.requestRefresh()
        if (changed.length) {
          const messages = await wire.fetchMessages(changed)
          if (!this.visible || this.restoring || this.accessLost || destroyed) return
          messages.forEach(message => this.applyMessage(message, 'updated'))
        }
        result.messages.forEach(message => this.applyMessage(message, 'created'))
      } catch (_) {
        if (!this.accessLost) await this.requestRefresh()
      } finally {
        catchingUp = false
        if (catchupPending && !this.failedAction) this.catchUp()
      }
    },

    mutateTimeline(callback) {
      if (window.Alpine?.mutateDom) window.Alpine.mutateDom(callback)
      else callback()
    },

    applyTombstone(row, updatedAt) {
      if (row.dataset.deleted === 'true') return
      const position = this.capturePosition()
      const id = Number(row.dataset.messageId)
      if (this.editingId === id) {
        this.editSaving = false
        this.cancelEdit()
      }
      const template = root.querySelector('[data-test="conversation-template-' + (row.dataset.own === 'true' ? 'own' : 'foreign') + '-deleted"]')
      if (!template) return
      this.mutateTimeline(() => {
        const body = template.content.querySelector('[data-test="conversation-message-body"]').cloneNode(true)
        body.setAttribute('wire:key', 'channel-deleted-body-' + id)
        const oldBody = row.querySelector('[data-test="conversation-message-body"]')
        window.Alpine?.destroyTree?.(oldBody)
        oldBody?.replaceWith(body)
        row.querySelectorAll('[data-test="conversation-edit-form"], [x-ref="container"]').forEach(element => {
          window.Alpine?.destroyTree?.(element)
          element.remove()
        })
        row.querySelector('[data-test="conversation-message-edited"]')?.remove()
        row.dataset.deleted = 'true'
        row.dataset.updatedAt = updatedAt
      })
      this.restorePosition(position)
    },

    applyMessage(message, kind = 'created') {
      if (this.accessLost || this.restoring || destroyed || !root?.isConnected ||
        message?.tenant_id !== tenantId || message?.channel_id !== channelId ||
        !Number.isSafeInteger(message.id) || message.id < 1 || typeof message.deleted !== 'boolean' ||
        typeof message.edited !== 'boolean' || (!message.deleted && typeof message.body !== 'string')) return
      const list = root.querySelector('[data-test="conversation-list"]')
      if (!list) return
      const existing = list.querySelector('[data-message-id="' + message.id + '"]')
      if (existing) {
        if (kind === 'created' || existing.dataset.deleted === 'true') return
        if (Date.parse(message.updated_at) < Date.parse(existing.dataset.updatedAt)) return
        if (!message.deleted && message.updated_at === existing.dataset.updatedAt &&
          message.edited === (existing.dataset.edited === 'true') &&
          message.body === existing.querySelector('[data-test="conversation-message-body"]').textContent) return
      }
      root.querySelectorAll('[data-test="conversation-message-actions"][aria-expanded="true"]')
        .forEach(button => button.click())
      if (existing) {
        if (message.deleted) return this.applyTombstone(existing, message.updated_at)
        const position = this.capturePosition()
        existing.querySelector('[data-test="conversation-message-body"]').textContent = message.body
        existing.dataset.updatedAt = message.updated_at
        existing.dataset.edited = String(message.edited)
        this.updateTime(existing, message)
        this.restorePosition(position)
        return
      }
      if (kind === 'updated') return
      const own = message.sender_user_id === Number(window.user?.id)
      const template = root.querySelector('[data-test="conversation-template-' + (own ? 'own' : 'foreign') + (message.deleted ? '-deleted' : '') + '"]')
      if (!template) return
      const position = this.capturePosition()
      const row = template.content.firstElementChild.cloneNode(true)
      row.dataset.messageId = String(message.id)
      row.dataset.updatedAt = message.updated_at
      row.dataset.edited = String(message.edited)
      row.querySelectorAll('[wire\\:key]').forEach(element => {
        element.setAttribute('wire:key', element.getAttribute('wire:key').replace(/-0$/, '-' + message.id))
      })
      row.setAttribute('wire:key', 'channel-message-' + message.id)
      row.querySelectorAll('[id], [for], [aria-describedby], [data-message-id]').forEach(element => {
        for (const attribute of ['id', 'for', 'aria-describedby', 'data-message-id']) {
          if (!element.hasAttribute(attribute)) continue
          element.setAttribute(attribute, element.getAttribute(attribute).replace(/0$/, String(message.id)))
        }
      })
      const fragments = [...row.querySelectorAll('template')].map(element => element.content)
      while (fragments.length) {
        const fragment = fragments.pop()
        fragment.querySelectorAll('[data-message-id]').forEach(element => { element.dataset.messageId = String(message.id) })
        fragments.push(...[...fragment.querySelectorAll('template')].map(element => element.content))
      }
      if (message.author_name !== null) row.querySelector('[data-test="conversation-message-author"]').textContent = message.author_name
      if (!message.deleted) row.querySelector('[data-test="conversation-message-body"]').textContent = message.body
      if (message.avatar) {
        let avatar = row.querySelector('[data-avatar]')
        if (message.avatar.image) {
          const image = root.querySelector('[data-test="conversation-template-avatar-image"]').content.firstElementChild.cloneNode(true)
          image.src = message.avatar.image
          avatar.replaceWith(image)
          avatar = image
        } else {
          avatar.textContent = message.avatar.initials
          avatar.style.backgroundColor = message.avatar.color
        }
        avatar.title = message.avatar.label
      }
      this.updateTime(row, message)
      this.mutateTimeline(() => {
        list.querySelector('[data-test="conversation-empty"]')?.remove()
        const following = [...list.querySelectorAll('[data-message-id]')]
          .find(element => Number(element.dataset.messageId) > message.id)
        const precedingDivider = following?.previousElementSibling
        list.insertBefore(row, precedingDivider?.dataset.test === 'conversation-date-divider' ? precedingDivider : following || null)
        window.Alpine?.initTree?.(row)
        this.rebuildDates()
      })
      const rows = [...list.querySelectorAll('[data-message-id]')]
      root.dataset.displayedThroughId = rows.at(-1)?.dataset.messageId || ''
      root.dataset.oldestId = rows[0]?.dataset.messageId || '0'
      if (position?.atEnd && this.visible) this.scrollLatest()
      else this.restorePosition(position)
      lastPosition = this.capturePosition()
      this.markDisplayed()
    },

    localDate(createdAt) {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: root.dataset.timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date(createdAt))
      const values = Object.fromEntries(parts.map(part => [part.type, part.value]))
      return values.year + '-' + values.month + '-' + values.day
    },

    updateTime(row, message) {
      const time = row.querySelector('[data-test="conversation-message-time"]')
      time.dateTime = message.created_at
      time.textContent = new Intl.DateTimeFormat('de-DE', {
        timeZone: root.dataset.timezone || 'UTC', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).format(new Date(message.created_at))
      if (message.edited && !message.deleted) {
        const marker = document.createElement('span')
        marker.dataset.test = 'conversation-message-edited'
        marker.textContent = root.querySelector('[data-test="conversation-edited-label"]').content.textContent
        time.append(' ', marker)
      }
    },

    rebuildDates() {
      const list = root.querySelector('[data-test="conversation-list"]')
      list.querySelectorAll('[data-test="conversation-date-divider"]').forEach(element => element.remove())
      let previousDate = null
      list.querySelectorAll('[data-message-id]').forEach(row => {
        const date = this.localDate(row.querySelector('[data-test="conversation-message-time"]').dateTime)
        if (date === previousDate) return
        previousDate = date
        const divider = root.querySelector('[data-test="conversation-template-date"]').content.firstElementChild.cloneNode(true)
        divider.setAttribute('wire:key', 'channel-date-' + row.dataset.messageId)
        divider.dataset.date = date
        const time = divider.querySelector('time')
        time.dateTime = date
        time.textContent = date.split('-').reverse().join('.')
        const newMarker = row.previousElementSibling
        list.insertBefore(divider, newMarker?.dataset.test === 'conversation-new-divider' ? newMarker : row)
      })
    },

    showErrors(errors) {
      this.editError = errors?.edit_body?.[0] || ''
      this.validationError = Object.entries(errors || {}).filter(([field]) => field !== 'edit_body').map(([, messages]) => messages[0]).join(' ')
    },

    async sendMessage() {
      if (this.busy || !navigator.onLine || this.accessLost || this.restoring) return
      const wire = this._livewire()
      if (!wire) return
      this.validationError = ''
      try {
        const message = await wire.send()
        if (!message) return
        this.mutateTimeline(() => {
          root.querySelectorAll('[data-test="conversation-new-divider"], [data-test="conversation-new-above"]')
            .forEach(marker => marker.remove())
        })
        const existing = root.querySelector('[data-test="conversation-list"] [data-message-id="' + message.id + '"]')
        this.applyMessage(message, existing ? 'updated' : 'created')
        this.scrollLatest()
        this.markDisplayed()
        this.resizeComposer(root.querySelector('[data-test="conversation-body"]'))
        root.querySelector('[data-test="conversation-body"]')?.focus({ preventScroll: true })
      } catch (_) {
        // Failed requests retain the server-bound draft and UUID for retry.
      }
    },

    async requestRefresh(kind = 'created') {
      if (destroyed || this.accessLost || !this.visible) return
      if (this.restoring) kind = 'restore'
      const priority = { created: 1, updated: 2, reenter: 3, restore: 4 }
      if (pendingRefresh && priority[pendingRefresh] > priority[kind]) kind = pendingRefresh
      if (kind === 'reenter' && this.editingId !== null) {
        deferredReentry = true
        return this.scheduleCatchup()
      }
      if (refreshing || this.busy) {
        pendingRefresh = kind
        return
      }
      const wire = this._livewire()
      if (!wire) return
      pendingRefresh = null
      refreshing = true
      refreshKind = kind
      if (kind === 'reenter') deferredReentry = false
      try {
        if (kind === 'reenter') await wire.reenterConversation()
        else await wire.refreshCurrentMessages()
      } catch (_) {
        // The interceptor supplies the localized error and keeps the draft.
      } finally {
        refreshing = false
        if (pendingRefresh && !this.failedAction) this.requestRefresh()
        if (catchupPending && !this.failedAction) this.scheduleCatchup()
      }
    },

    capturePosition() {
      const list = root?.querySelector('[data-test="conversation-messages"]')
      if (!list) return null
      const top = list.getBoundingClientRect().top
      const anchor = [...list.querySelectorAll('[data-message-id]')]
        .find(message => message.getBoundingClientRect().bottom > top)
      return {
        scrollTop: list.scrollTop,
        atEnd: Math.abs(list.scrollTop) <= 48,
        anchorId: anchor?.dataset.messageId,
        anchorOffset: anchor ? anchor.getBoundingClientRect().top - top : null,
        openingVersion: root.dataset.openingVersion,
        olderFocused: document.activeElement?.matches('[data-test="conversation-older"]')
      }
    },

    restorePosition(position) {
      const list = root?.querySelector('[data-test="conversation-messages"]')
      if (!list || !position) return
      olderIntent = false
      draggingScrollbar = false
      const anchor = position.anchorId && list.querySelector(`[data-message-id="${position.anchorId}"]`)
      if (anchor) {
        list.scrollTop += anchor.getBoundingClientRect().top - list.getBoundingClientRect().top - position.anchorOffset
      } else list.scrollTop = position.scrollTop
    },

    afterRender(kind, position) {
      if (destroyed || !root?.isConnected || (this.failedAction && kind !== 'restore')) return
      if (this.restoring && kind === 'restore') {
        this.restoring = false
        delete root.dataset.conversationRestore
        if (!opened) this.initializeViewport()
        root.querySelector('[data-test="conversation-messages"]')?.style.removeProperty('visibility')
        if (this.visible) this.subscribe()
      }
      this.reconcileEdit()
      if (!this.visible) return
      if (kind === 'send') this.scrollLatest()
      else if (kind === 'reenter' && position?.openingVersion !== root.dataset.openingVersion) this.alignEntry()
      else if (kind === 'created' && position?.atEnd) this.scrollLatest()
      else if (kind !== 'markRead') this.restorePosition(position)
      lastScrollTop = root.querySelector('[data-test="conversation-messages"]')?.scrollTop || 0
      lastPosition = this.capturePosition()
      if (kind === 'send') root.querySelector('[data-test="conversation-body"]')?.focus({ preventScroll: true })
      else if (kind === 'olderMessages' && position?.olderFocused) {
        const target = root.querySelector('[data-test="conversation-older"]') ||
          root.querySelector('[data-test="conversation-messages"]')
        this.$nextTick(() => target?.focus({ preventScroll: true }))
      }
    },

    timelineScrolled(event) {
      const list = root?.querySelector('[data-test="conversation-messages"]')
      if (!list) return
      if (draggingScrollbar && event?.isTrusted && list.scrollTop < lastScrollTop) this.upwardIntent(event)
      lastScrollTop = list.scrollTop
      if (this.visible) lastPosition = this.capturePosition()
      if (olderVisible && olderIntent) this.loadOlder()
      this.markDisplayed()
    },

    upwardIntent(event) {
      const list = root?.querySelector('[data-test="conversation-messages"]')
      if (!event.isTrusted || !opened || !this.visible || !list || list.scrollHeight <= list.clientHeight) return
      olderIntent = true
      if (olderVisible) this.loadOlder()
    },

    wheelIntent(event) {
      if (event.deltaY < 0) this.upwardIntent(event)
      else olderIntent = false
    },

    touchStarted(event) {
      touchY = event.touches[0]?.clientY ?? null
      olderIntent = false
    },

    touchMoved(event) {
      const nextY = event.touches[0]?.clientY ?? null
      if (touchY !== null && nextY !== null && nextY > touchY) this.upwardIntent(event)
      touchY = nextY
    },

    keyboardIntent(event) {
      if (event.target.closest('input, textarea, [contenteditable="true"]') || event.ctrlKey || event.metaKey ||
        event.altKey) return
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey)) {
        this.upwardIntent(event)
      } else olderIntent = false
    },

    scrollbarStarted(event) {
      const list = root?.querySelector('[data-test="conversation-messages"]')
      draggingScrollbar = event.isTrusted && event.button === 0 && event.target === list
    },

    scrollbarEnded() {
      draggingScrollbar = false
    },

    olderIntersect(visible = true) {
      olderVisible = visible
      if (visible && olderIntent) this.loadOlder()
    },

    async loadOlder(focusButton = false) {
      if (focusButton) this.retainOlderFocus()
      if (destroyed || !opened || !this.visible || !navigator.onLine || this.busy || refreshing ||
        this.olderLoading || (!focusButton && !olderIntent) || root?.dataset.hasOlder !== 'true') return
      const wire = this._livewire()
      if (!wire) return
      const position = this.capturePosition()
      if (position) position.olderFocused ||= focusButton
      olderPosition = position
      olderIntent = false
      this.olderLoading = true
      try {
        await wire.olderMessages()
      } catch (_) {
        // An explicit button retry remains available after a failed soft load.
      } finally {
        this.olderLoading = false
        olderPosition = null
        if (position?.olderFocused && this.visible && !destroyed && root?.isConnected) {
          await this.$nextTick(() => {
            const target = root.querySelector('[data-test="conversation-older"]') ||
              root.querySelector('[data-test="conversation-messages"]')
            target?.focus({ preventScroll: true })
          })
        }
      }
    },

    retainOlderFocus() {
      if (olderPosition) olderPosition.olderFocused = true
    },

    actionExecuted(detail) {
      const response = detail?.response
      if (response?.tenant_id !== tenantId || response?.channel_id !== channelId ||
        !Number.isSafeInteger(response?.channel_message_id)) return
      if (response.channel_message_id === this.editingId) this.cancelEdit()
      if (response.channel_message) this.applyMessage(response.channel_message, 'updated')
    },

    beginEdit(messageId) {
      if (this.editSaving) return
      const position = this.capturePosition()
      olderIntent = false
      this.editingId = messageId
      this.editError = ''
      this.editDraft = root.querySelector(`[data-message-id="${messageId}"] [data-test="conversation-message-body"]`)
        ?.textContent || ''
      queueMicrotask(() => this.restorePosition(position))
      this.$nextTick(() => root.querySelector(`[data-message-id="${messageId}"] [data-test="conversation-edit-body"]`)
        ?.focus({ preventScroll: true }))
    },

    cancelEdit() {
      if (this.editSaving) return
      const position = this.capturePosition()
      const messageId = this.editingId
      this.editingId = null
      this.editDraft = ''
      this.editComposing = false
      queueMicrotask(() => {
        this.restorePosition(position)
        if (deferredReentry) this.requestRefresh('reenter')
      })
      this.$nextTick(() => root.querySelector(`[data-message-id="${messageId}"] [data-test="conversation-message-actions"]`)
        ?.focus({ preventScroll: true }))
    },

    reconcileEdit() {
      if (this.editingId === null || this.editSaving) return
      const message = root.querySelector(`[data-message-id="${this.editingId}"]`)
      if (!message || message.dataset.deleted === 'true') this.cancelEdit()
    },

    async saveEdit() {
      if (this.editingId === null || this.busy || this.editSaving || !navigator.onLine) return
      const wire = this._livewire()
      if (!wire) return
      this.editSaving = true
      this.editError = ''
      try {
        const saved = await wire.editMessage(this.editingId, this.editDraft)
        this.editSaving = false
        if (saved) {
          this.applyMessage(saved, 'updated')
          this.cancelEdit()
        }
      } catch (_) {
        // Failed requests retain the edit draft for an explicit retry.
      } finally {
        this.editSaving = false
        this.reconcileEdit()
      }
    },

    editKeydown(event) {
      if (event.isComposing || this.editComposing || event.keyCode === 229) return
      if (event.key === 'Escape') {
        event.preventDefault()
        this.cancelEdit()
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.saveEdit()
      }
    },

    markDisplayed() {
      const through = Number(root?.dataset.displayedThroughId)
      if (destroyed || this.accessLost || this.restoring || !this.visible || !this.capturePosition()?.atEnd || !through ||
        through <= markedThrough) return
      pendingReceipt = Math.max(pendingReceipt, through)
      if (receiptTimer || marking) return
      receiptTimer = setTimeout(() => {
        receiptTimer = null
        this.flushReceipt()
      }, Math.max(0, 2000 - (Date.now() - lastReceiptAt || 0)))
    },

    async flushReceipt() {
      clearTimeout(receiptTimer)
      receiptTimer = null
      if (destroyed || this.accessLost || marking || pendingReceipt <= markedThrough || !pendingReceipt) return
      const wire = this._livewire()
      if (!wire) return
      const through = pendingReceipt
      pendingReceipt = 0
      marking = true
      lastReceiptAt = Date.now()
      try {
        const acknowledged = await wire.markRead(through)
        if (acknowledged && !this.failedAction) {
          markedThrough = through
          if (Number(root?.dataset.displayedThroughId) === through) root.dataset.unreadCount = '0'
        }
      } catch (_) {
        // Reopening or the next visible refresh retries an unacknowledged watermark.
      } finally {
        marking = false
        if (pendingReceipt > markedThrough && this.visible) this.markDisplayed()
      }
    },

    scrollLatest() {
      if (!this.visible) return
      const list = root?.querySelector('[data-test="conversation-messages"]')
      olderIntent = false
      draggingScrollbar = false
      if (list) list.scrollTop = 0
    },

    resizeComposer(element) {
      if (!element) return
      const style = getComputedStyle(element)
      const lineHeight = parseFloat(style.lineHeight)
      const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
      // Measure wrapped rows before applying the minimum height or single-line inset.
      element.style.setProperty('padding-block', '12px', 'important')
      element.style.minHeight = '0px'
      element.style.height = 'auto'
      const contentHeight = element.scrollHeight
      if (contentHeight <= lineHeight + 25) {
        element.style.setProperty('padding-block', `${Math.max(12, (60 - lineHeight - borders) / 2)}px`, 'important')
      }
      element.style.removeProperty('min-height')
      element.style.height = `${Math.max(60, Math.min(contentHeight, 160))}px`
      element.style.overflowY = element.scrollHeight > 160 ? 'auto' : 'hidden'
    },

    messageMenuTab(event, trigger, panel, focusables, close) {
      const buttons = [...panel.querySelectorAll('button:not(:disabled)')]
      if (event.target === trigger) {
        if (event.shiftKey) return close()
        event.preventDefault()
        if (trigger.getAttribute('aria-expanded') === 'true' && panel.isConnected) {
          buttons[0]?.focus({ preventScroll: true })
        }
      } else if (event.target === buttons[0] && event.shiftKey) {
        event.preventDefault()
        close()
        trigger.focus({ preventScroll: true })
      } else if (event.target === buttons.at(-1) && !event.shiftKey) {
        const outside = focusables.filter(element => !panel.contains(element) &&
          element.tabIndex >= 0 && element.getClientRects().length)
        const next = outside[outside.indexOf(trigger) + 1]
        event.preventDefault()
        close()
        next?.focus()
      }
    },

    concealForRestore() {
      if (destroyed || this.accessLost || !root?.isConnected) return
      root.dataset.conversationRestore = 'true'
      const list = root.querySelector('[data-test="conversation-messages"]')
      if (list) list.style.visibility = 'hidden'
      this.restoring = true
      this.flushReceipt()
      this.leaveChannel()
    },

    clearContents() {
      this.accessLost = true
      this.restoring = false
      pendingRefresh = null
      pendingSend = false
      this.editingId = null
      this.editDraft = ''
      clearTimeout(receiptTimer)
      pendingReceipt = 0
      window.Livewire?.all?.().forEach(dialog => {
        if (dialog.actionName === 'delete_channel_message' && dialog.actionContext?.tenant_id === tenantId &&
          dialog.actionContext?.channel_id === channelId) dialog.$set('visible', false, false)
      })
      const list = root.querySelector('[data-test="conversation-list"]')
      if (list) {
        window.Alpine?.destroyTree?.(list)
        list.replaceChildren()
      }
      root.querySelector('[data-test="conversation-composer"]')?.remove()
      root.querySelector('[data-test="conversation-context"]')?.remove()
      this.leaveChannel()
    },

    composerKeydown(event) {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing || this.composing || event.keyCode === 229) return
      event.preventDefault()
      if (!navigator.onLine) return
      if (this.busy) {
        if (!this.sending) pendingSend = true
      } else {
        event.target.form?.requestSubmit()
      }
    },

    destroy() {
      destroyed = true
      clearTimeout(receiptTimer)
      this.leaveChannel()
      unsubscribe?.()
      unsubscribe = null
      unsubscribeMessage?.()
      unsubscribeMessage = null
      document.removeEventListener('visibilitychange', visibilityHandler)
      window.removeEventListener('online', onlineHandler)
      window.removeEventListener('offline', visibilityHandler)
      document.removeEventListener('livewire:navigating', navigatingHandler)
      window.removeEventListener('pagehide', pageHideHandler)
      window.removeEventListener('pageshow', pageShowHandler)
    }
  }
}
